export interface LookDelta {
  x: number;
  y: number;
}

export interface CameraAngles {
  yaw: number;
  pitch: number;
}

export interface GroundPoint {
  x: number;
  z: number;
}

export const DEFAULT_LOOK_SENSITIVITY = 0.65;
export const MIN_LOOK_SENSITIVITY = 0.35;
export const MAX_LOOK_SENSITIVITY = 1.25;
export const MAX_LOOK_DELTA_PER_FRAME = 160;
export const MAX_PITCH = 1.12;

const YAW_RADIANS_PER_PIXEL = 0.00215;
const PITCH_RADIANS_PER_PIXEL = 0.00185;
const FULL_TURN = Math.PI * 2;

export function boundLookDelta(
  delta: LookDelta,
  maximum = MAX_LOOK_DELTA_PER_FRAME,
): LookDelta {
  if (!Number.isFinite(delta.x) || !Number.isFinite(delta.y)) {
    return { x: 0, y: 0 };
  }

  const limit =
    Number.isFinite(maximum) && maximum > 0
      ? maximum
      : MAX_LOOK_DELTA_PER_FRAME;
  const length = Math.hypot(delta.x, delta.y);
  if (length <= limit) return { ...delta };

  const scale = limit / length;
  return {
    x: delta.x * scale,
    y: delta.y * scale,
  };
}

export function clampLookSensitivity(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_LOOK_SENSITIVITY;
  return Math.min(MAX_LOOK_SENSITIVITY, Math.max(MIN_LOOK_SENSITIVITY, value));
}

export function applyCameraLook(
  angles: CameraAngles,
  delta: LookDelta,
  sensitivity: number,
): CameraAngles {
  const look = boundLookDelta(delta);
  const scale = clampLookSensitivity(sensitivity);
  const yaw = normalizeYaw(angles.yaw - look.x * YAW_RADIANS_PER_PIXEL * scale);
  const pitch = Math.min(
    MAX_PITCH,
    Math.max(
      -MAX_PITCH,
      angles.pitch - look.y * PITCH_RADIANS_PER_PIXEL * scale,
    ),
  );

  return { yaw, pitch };
}

export function normalizeYaw(yaw: number): number {
  if (!Number.isFinite(yaw)) return 0;
  return ((yaw + Math.PI) % FULL_TURN + FULL_TURN) % FULL_TURN - Math.PI;
}

export function yawToward(origin: GroundPoint, target: GroundPoint): number {
  return Math.atan2(-(target.x - origin.x), -(target.z - origin.z));
}
